import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFormikContext } from 'formik';
import {
  FC,
  InputHTMLAttributes,
  ReactElement,
  useEffect,
  useRef,
} from 'react';
import { Box } from 'src/components/box';
import { DropdownContent } from 'src/components/dropdown';
import { Calendar } from 'src/components/form/datepicker/calendar';
import {
  DateInputField,
  ErrorWrapper,
} from 'src/components/form/datepicker/input-field';
import {
  InputTime,
  transformValueToInputMask,
  validateDateInput,
} from 'src/components/form/datepicker/utils';
import { ErrorMessage } from 'src/components/form/error-message';
import { useFieldGroup } from 'src/components/form/field-group';
import { FieldItem, FieldLayout } from 'src/components/form/field-layout';
import { Label } from 'src/components/form/label';
import { useCustomField } from 'src/components/form/use-custom-field';
import { Hint } from 'src/components/hint';
import { Tooltip } from 'src/components/tooltip';
import { useBreakpoints } from 'src/hooks/use-breakpoints';
import { useDropdown } from 'src/hooks/use-dropdown';
import { useId } from 'src/hooks/use-id';

type DateInputProps = {
  name: string;
  label: string;
  /**
   * @deprecated Use this pattern to create custom error messages.
   *
   * ```
   * import { number } from 'yup';
   *
   * const validation = number
   *   .test(
   *     'No17',
   *     () => (label: string) => `${label} can't be 17.`,
   *     (value, context) => value !== 17
   *   );
   * ```
   */
  getCustomErrorMessage?: (message: string) => string | undefined;
  time?: InputTime;
  minBookingDate?: Date;
  maxBookingDate?: Date;
  hideLabel?: boolean;
  hint?: ReactElement | string;
  info?: ReactElement | string;
  nested?: boolean;
  stacked?: boolean;
} & InputHTMLAttributes<HTMLInputElement>;

export const DateInput: FC<DateInputProps> = (props) => {
  const { minTablet } = useBreakpoints();

  const {
    time = false,
    name,
    label,
    hideLabel,
    hint,
    info,
    nested,
    stacked = !minTablet,
    minBookingDate,
    maxBookingDate,
    getCustomErrorMessage,
    ...inputProps
  } = props;

  const { setFieldValue, setFieldTouched } = useFormikContext<unknown>();
  const validate = (value: string | null) => validateDateInput(time, value);

  const id = useId();
  const valueRef = useRef('');
  const setValue = (value: string) => (valueRef.current = value);
  const [field, showError, error] = useCustomField<string | null>(name, label, {
    validate() {
      return validate(valueRef.current);
    },
  });

  const formGroup = useFieldGroup();
  useEffect(() => {
    if (formGroup) {
      formGroup.onError({ name, showError });
    }
  }, [showError, formGroup, name]);

  const { active, setActive, setWrapperElement, ...dropdown } = useDropdown({
    placement: 'bottom-start',
  });

  // Set initial value
  useEffect(() => {
    setValue(transformValueToInputMask(field.value, time));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // field can be set from the outside, so we need to sync it with input
  useEffect(() => {
    const hasError = Boolean(validate(valueRef.current));
    // when there is no formik value, but an error, the user is probably still typing
    if (!field.value && hasError) return;
    const newValue = transformValueToInputMask(field.value, time);
    if (newValue === valueRef.current) return;
    setValue(newValue);
  }, [field.value]); // eslint-disable-line react-hooks/exhaustive-deps

  const fieldId = formGroup?.id || id;

  const ariaLabel = hideLabel ? label : undefined;

  const fieldItem = (
    <FieldItem>
      <div ref={setWrapperElement}>
        <DateInputField
          aria-label={ariaLabel}
          {...inputProps}
          referenceRef={dropdown.refs.setReference}
          time={time}
          fieldId={id}
          name={name}
          value={valueRef.current}
          setUserValue={setValue}
          showError={showError}
          setActive={setActive}
          validate={validate}
          onTab={() => setActive(false)}
          onBlur={field.onBlur}
        />

        {active && (
          <DropdownContent
            ref={dropdown.refs.setFloating}
            style={dropdown.style}
          >
            <Box>
              <Calendar
                time={time}
                value={field.value ? new Date(field.value) : null}
                minBookingDate={minBookingDate}
                maxBookingDate={maxBookingDate}
                onSelect={(date) => {
                  const dateString = date && date.toISOString();

                  if (dateString !== field.value) {
                    setValue(transformValueToInputMask(dateString, time));
                    setFieldTouched(name, true, false);
                    setFieldValue(name, dateString, true);
                  }

                  // Close only when the user do not need to specify a time.
                  if (!time) {
                    setActive(false);
                  }
                }}
              />
              {hint && (
                <Hint
                  disabled={props.disabled}
                  children={hint}
                  mode="formInput"
                />
              )}
              {showError && error && (
                <ErrorWrapper>
                  <ErrorMessage
                    error={error}
                    label={label}
                    getCustomErrorMessage={getCustomErrorMessage}
                  />
                </ErrorWrapper>
              )}
            </Box>
          </DropdownContent>
        )}
      </div>

      {hint && (
        <Hint disabled={props.disabled} children={hint} mode="formInput" />
      )}

      {showError && error && (
        <ErrorMessage
          data-testid={`${name}Error`}
          error={error}
          label={label}
          getCustomErrorMessage={getCustomErrorMessage}
        />
      )}
    </FieldItem>
  );

  return ((formGroup && !minTablet) || !formGroup) && !hideLabel ? (
    <FieldLayout stacked={stacked}>
      <Label htmlFor={fieldId} error={showError} nested={nested}>
        {label}

        {info && (
          <>
            &nbsp;
            <Tooltip content={info}>
              {(targetProps) => (
                <FontAwesomeIcon {...targetProps} icon={faInfoCircle} />
              )}
            </Tooltip>
          </>
        )}
      </Label>

      {fieldItem}
    </FieldLayout>
  ) : (
    fieldItem
  );
};
